import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Loader2, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormPhoneField } from '@/components/forms/form-phone-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import {
  CONTACT_PURPOSE_OPTIONS,
  contactFormSchema,
  type ContactFormValues,
} from '@/components/contact/contact-form-schema';
import { useTenant } from '@/providers/tenant-context';
import { SALES_PHONE, SALES_PHONE_DISPLAY } from '@/pages/landing/constants';

interface ContactFormSectionProps {
  variant?: 'default' | 'demo';
}

export function ContactFormSection({ variant = 'default' }: ContactFormSectionProps) {
  const { tenant, isWhitelabel } = useTenant();
  const brandName = isWhitelabel && tenant?.name ? tenant.name : 'Milora';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  const onSubmit = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      const phone = values.phone.trim();
      await axiosInstance.post('/api/contact', {
        ...values,
        phone: phone ? (phone.startsWith('+') ? phone : `+${phone}`) : undefined,
      });
      setIsSuccess(true);
      toast.success('Message sent successfully!');
      form.reset();
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDemo = variant === 'demo';

  return (
    <section id="contact" className="py-24 relative overflow-hidden scroll-mt-24">
      <div className="container px-6 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">
              {isDemo ? 'Request a demo' : 'Get in touch'}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {isDemo
                ? `See how ${brandName} can help you run your community, capture leads, and grow engagement. Our team typically responds within one business day.`
                : `Have questions about ${brandName}? Want to request a feature or need support? Fill out the form and we'll get back to you as soon as possible.`}
            </p>

            <div className="space-y-6 pt-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold">Call Us</h4>
                  <a
                    href={`tel:${SALES_PHONE}`}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {SALES_PHONE_DISPLAY}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-bold">Email Us</h4>
                  <p className="text-muted-foreground">simpliumtechnologies@gmail.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-primary/10 rounded-3xl blur-3xl -z-10" />
            <div className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-3xl p-8 shadow-2xl">
              {isSuccess ? (
                <div className="h-[400px] flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in">
                  <div className="w-20 h-20 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-10 w-10" />
                  </div>
                  <h3 className="text-2xl font-bold">Message Sent!</h3>
                  <p className="text-muted-foreground">
                    Thank you for reaching out. We&apos;ll be in touch shortly.
                  </p>
                  <Button variant="outline" onClick={() => setIsSuccess(false)} className="mt-4">
                    Send another message
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormInputField
                        control={form.control}
                        name="name"
                        label="Name"
                        placeholder="John Doe"
                        required
                      />
                      <FormInputField
                        control={form.control}
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="john@example.com"
                        required
                      />
                    </div>

                    <FormPhoneField
                      control={form.control}
                      name="phone"
                      label="Phone"
                      placeholder="+91 98765 43210"
                      defaultCountry="IN"
                    />

                    <FormSelectField
                      control={form.control}
                      name="purpose"
                      label="Purpose"
                      placeholder="Select a topic"
                      options={[...CONTACT_PURPOSE_OPTIONS]}
                      required
                    />

                    <FormTextareaField
                      control={form.control}
                      name="message"
                      label="Message"
                      placeholder="How can we help you?"
                      className="min-h-[120px]"
                      maxLength={1000}
                      required
                    />

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-base font-semibold shadow-lg shadow-primary/20"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
